/**
 * Cloudscape application shell: TopNavigation + AppLayoutToolbar with
 * role-aware SideNavigation and Flashbar.
 *
 * This is the AgentMesh SPA's new top-level layout. Replaces the prior
 * hand-rolled `Layout.tsx` + `RoleNav.tsx` + ad-hoc per-page banners.
 *
 * Component composition (driven by the Venue design at
 * https://venue.aws.dev/projects/650d42c7-7be5-46cc-9807-419ac6113978):
 *
 *   - `TopNavigation` carries the brand, a stage badge, the user
 *     identity dropdown with a Sign out action, and the dark-mode
 *     toggle.
 *   - `AppLayoutToolbar` renders the page content in the centre
 *     column, `SideNavigation` in the left rail (public items only for
 *     signed-out visitors; role sections once signed in), and
 *     `Flashbar` in the notifications slot.
 *   - Active side-nav highlight follows `react-router`'s
 *     `useLocation`; clicks call `useNavigate` instead of letting the
 *     anchor navigate so the SPA stays single-page.
 *
 * Read-aloud accessibility: the TopNavigation's Stage badge carries an
 * `aria-label` so screen-reader users hear `Stage: dev` / `Stage: prod`
 * matching R13.4's literal-string requirement.
 *
 * The non-production demonstration banner that previously sat between
 * the TopNavigation and the AppLayout has been removed at user
 * request; the demo nature is communicated by the persistent Stage
 * badge in the TopNavigation utilities.
 */

import {
  AppLayoutToolbar,
  Flashbar,
  SideNavigation,
  type SideNavigationProps,
  TopNavigation,
  type TopNavigationProps,
} from "@cloudscape-design/components";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { isAuthConfigured, stage } from "../config";
import { useFlashbar } from "../theme/FlashbarContext";
import { useTheme } from "../theme/ThemeContext";

/* ------------------------------------------------------------------ */
/*  Side navigation definitions                                        */
/* ------------------------------------------------------------------ */

const GENERAL_ITEMS: SideNavigationProps.Item[] = [
  { type: "link", text: "Home", href: "/" },
  { type: "link", text: "Catalog", href: "/catalog" },
  { type: "link", text: "Skill catalog", href: "/skills" },
  { type: "link", text: "Examples", href: "/examples" },
  { type: "link", text: "About", href: "/about" },
  { type: "link", text: "Design choices", href: "/about/design" },
];

/**
 * Routes that carry no `RouteGuard` — what a signed-out visitor can
 * reach. Shown as the whole side nav before sign-in so presenters can
 * move between the landing page, the examples, and the deep dives
 * without signing in first.
 */
const PUBLIC_ITEMS: SideNavigationProps.Item[] = [
  { type: "link", text: "Home", href: "/" },
  { type: "link", text: "Examples", href: "/examples" },
  { type: "link", text: "About", href: "/about" },
  { type: "link", text: "Design choices", href: "/about/design" },
];

const ADMIN_ITEMS: SideNavigationProps.Item[] = [
  { type: "link", text: "Catalog", href: "/admin/catalog" },
  { type: "link", text: "Pending", href: "/admin/pending" },
  { type: "link", text: "Submit", href: "/admin/submit" },
  { type: "link", text: "Skills", href: "/admin/skills" },
  { type: "link", text: "Policy", href: "/admin/policy" },
  { type: "link", text: "Observability", href: "/admin/observability" },
];

/* ------------------------------------------------------------------ */
/*  Shell                                                              */
/* ------------------------------------------------------------------ */

export function CloudscapeShell() {
  const { session, login, logout } = useAuth();
  const { mode, toggle: toggleTheme } = useTheme();
  const { items: flashItems } = useFlashbar();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = session?.isAdmin ?? false;
  const isConsumer = session?.isConsumer ?? false;

  const identityLabel =
    session?.claims.email ??
    session?.claims["cognito:username"] ??
    session?.claims.sub ??
    "";
  const roleLabel = isAdmin ? "Admin" : isConsumer ? "Consumer" : "no role";

  // Role-aware SideNavigation items. Admins see general + admin sections;
  // consumers see general only; everyone else (signed out, or signed in
  // with no role) sees just the public, unguarded routes.
  const sideNavItems: SideNavigationProps.Item[] = isAdmin
    ? [
        { type: "section", text: "General", items: GENERAL_ITEMS },
        { type: "section", text: "Admin", items: ADMIN_ITEMS },
      ]
    : isConsumer
      ? [{ type: "section", text: "General", items: GENERAL_ITEMS }]
      : PUBLIC_ITEMS;

  // TopNavigation utilities. Stage badge + theme toggle + user dropdown
  // (or Sign in button for unauthenticated visitors). When the bundle
  // is missing Cognito config (`isAuthConfigured()` false), substitute
  // a small disabled status pill so the click never silently no-ops.
  const stageUtility: TopNavigationProps.Utility = {
    type: "button",
    text: stage.toUpperCase(),
    ariaLabel: `Stage: ${stage}`,
    title: `Stage: ${stage}`,
  };
  const themeUtility: TopNavigationProps.Utility = {
    type: "button",
    iconName: mode === "dark" ? "star-filled" : "star",
    ariaLabel: `Switch to ${mode === "dark" ? "light" : "dark"} mode`,
    title: `Switch to ${mode === "dark" ? "light" : "dark"} mode`,
    onClick: () => toggleTheme(),
  };
  const sessionUtility: TopNavigationProps.Utility = session
    ? {
        type: "menu-dropdown",
        text: `${identityLabel} · ${roleLabel}`,
        description: identityLabel,
        iconName: "user-profile",
        items: [{ id: "signout", text: "Sign out" }],
        onItemClick: ({ detail }: { detail: { id: string } }) => {
          if (detail.id === "signout") logout();
        },
      }
    : isAuthConfigured()
      ? {
          type: "button",
          text: "Sign in",
          variant: "primary-button",
          onClick: () => login(),
        }
      : {
          type: "button",
          text: "Sign-in not configured",
          ariaLabel: "Sign-in not configured",
          title:
            "The deployed bundle is missing VITE_HOSTED_UI_DOMAIN / VITE_USER_POOL_CLIENT_ID. Re-run the frontend build with the Cognito stack outputs injected.",
        };

  // Single-page navigation: the SideNavigation links carry real `href`
  // values so screen readers and middle-click work, but on click we
  // call `navigate` to stay inside React Router. The default link
  // would do a full document load which would lose the auth session.
  const handleFollow: NonNullable<SideNavigationProps["onFollow"]> = (e) => {
    if (
      e.detail.external ||
      !e.detail.href ||
      e.detail.href.startsWith("http")
    ) {
      return;
    }
    e.preventDefault();
    navigate(e.detail.href);
  };

  return (
    <>
      <TopNavigation
        identity={{ title: "AgentMesh", href: "/" }}
        i18nStrings={{ overflowMenuTriggerText: "More", overflowMenuTitleText: "All" }}
        utilities={[stageUtility, themeUtility, sessionUtility]}
      />
      <AppLayoutToolbar
        contentType="default"
        toolsHide
        navigation={
          <SideNavigation
            header={{ text: "AgentMesh", href: "/" }}
            activeHref={location.pathname}
            items={sideNavItems}
            onFollow={handleFollow}
          />
        }
        notifications={
          flashItems.length > 0 ? <Flashbar items={flashItems} stackItems /> : undefined
        }
        content={<Outlet />}
      />
    </>
  );
}
